// Vorkasse (SEPA prepayment) configuration.
// Hardcoded consts, deliberately NO NEXT_PUBLIC_*, Next.js 16 Turbopack does
// not inline NEXT_PUBLIC_* into the checkout client bundle, which is why every
// payment flag in this repo (paygateto.ts, sellabroad.ts, paytree.ts) is a
// const. Rollback path: edit this file + deploy.

/**
 * Master switch. Live since 2026-07-23: channel flag allowUnpaidOrders is on
 * for eu, the loyalty/affiliate/stock-sync webhooks are on ORDER_FULLY_PAID
 * with a chargeStatus guard, and the cron plus sweeper are registered in the
 * server crontab (auth via the LOYALTY_CRON_SECRET fallback).
 */
export const VORKASSE_ENABLED = true;

/**
 * Channels that offer bank-transfer prepayment. eu settles EUR over SEPA,
 * us settles USD over ACH/Fedwire (owner decision 2026-07-25, extending the
 * original eu-only launch).
 */
export const VORKASSE_CHANNELS = ["eu", "us"] as const;
export type VorkasseChannelSlug = (typeof VORKASSE_CHANNELS)[number];

export function isVorkasseChannel(channel: string | null | undefined): channel is VorkasseChannelSlug {
	return typeof channel === "string" && (VORKASSE_CHANNELS as readonly string[]).includes(channel);
}

/** Settlement currency per channel; mirrors the Saleor channel configuration. */
export const VORKASSE_CHANNEL_CURRENCY: Record<VorkasseChannelSlug, "EUR" | "USD"> = {
	eu: "EUR",
	us: "USD",
};

/**
 * The SEPA channel. Kept as a named constant for the SEPA-only surfaces
 * (EPC QR, statement import) and as the historical fallback where no row
 * channel is available.
 */
export const VORKASSE_CHANNEL = "eu";

/** Days from placement to the §323 BGB deadline / auto cancellation. */
export const VORKASSE_DEADLINE_DAYS = 21;

/**
 * Orders above this gross total never see the Vorkasse tile. Applies in the
 * channel settlement currency: 1500 EUR on eu, 1500 USD on us.
 */
export const VORKASSE_MAX_ORDER = 1500;

/**
 * Shortfall up to this amount is absorbed: book the FULL order total anyway.
 * In the channel settlement currency (EUR on eu, USD on us).
 */
export const VORKASSE_TOLERANCE_UNDER = 1.0;

/**
 * Overpayment above this amount goes to OVERPAID_REVIEW instead of being
 * booked. In the channel settlement currency (EUR on eu, USD on us).
 */
export const VORKASSE_TOLERANCE_OVER = 5.0;

/** Hard cap on cancellations per cron run, a runaway loop cancels at most this many. */
export const VORKASSE_MAX_CANCELS_PER_RUN = 5;

/**
 * DB-side placement cap: open rows per email address.
 *
 * Was 1 until 2026-08-22, then 3, now 5. At 1 a repeat buyer was locked out of
 * Vorkasse for the whole VORKASSE_DEADLINE_DAYS window by a single unpaid
 * order, and the conversion read of 2026-08-22 found 5 of 6 buyers who hit that
 * wall never ordered again. The forensic read of 2026-09-02 found one more:
 * a repeat customer tried 18 times across four checkouts over six days and
 * never got through, because her first transfer sat unbooked for 16 days.
 *
 * 5 is affordable because the exposure is bounded by time, not only by count:
 * measured over all settled rows the median booking lag is 0.9 days and 56 of
 * 57 land inside 7 days, so five open rows per address is a state almost nobody
 * reaches by accident, while the cron cancels anything past the deadline. The
 * window itself is unchanged.
 */
export const VORKASSE_MAX_OPEN_PER_EMAIL = 5;

/**
 * DB-side placement cap: open rows per hashed IP.
 *
 * Kept in lockstep with the per-email cap on purpose. A single customer
 * ordering from one home connection consumes both counters at the same rate,
 * so a lower IP cap would silently become the binding limit and make the
 * per-email number above unreachable for exactly the repeat buyer it exists
 * for. Raise or lower the two together.
 */
export const VORKASSE_MAX_OPEN_PER_IP = 5;

/**
 * Refusal code the placement returns when the per-email cap is reached. Shared
 * with the checkout client, which maps exactly this code to a localized notice
 * plus a card CTA instead of rendering the server's raw German message and a
 * retry button that can never succeed.
 */
export const VORKASSE_CAP_EMAIL_CODE = "cap_email";

/**
 * If no operator has booked a payment for this many hours, the cron treats the
 * queue as unattended and suspends auto-cancellation (nobody was there to see
 * an incoming transfer).
 */
export const VORKASSE_OPERATOR_ABSENCE_HOURS = 72;

/**
 * Payee account. `holder` is the settlement agent, NOT BergdorfBio, that
 * mismatch is explained to the customer by the VoP block, never hidden.
 * `iban` is stored unspaced: it is grouped for display but copied unspaced,
 * because spaces break pasting in many banking apps.
 * `bankName` uses a plain hyphen instead of the en dash of the bank's own
 * spelling, so the whole file stays inside the project's ASCII punctuation rule.
 */
export const VORKASSE_BANK = {
	holder: "Kirk & Epstein Trading Limited",
	iban: "DE82202208000047788363",
	bic: "SXPYDEHH",
	bankName: "Banking Circle S.A. - German Branch",
} as const;

/**
 * US-channel payee account (Airwallex global account, USD). Same paying
 * agent as the SEPA account. `accountNumber` + `achRouting` cover the
 * standard transfer from a US bank; `fedwireRouting` and `swift` only
 * matter for wires and stay behind the wire-details disclosure.
 * All values stored digit-only/unspaced for clipboard safety, mirroring
 * the IBAN rule above.
 */
export const VORKASSE_BANK_US = {
	holder: "Kirk & Epstein Trading Limited",
	accountNumber: "8482155172",
	achRouting: "026073150",
	fedwireRouting: "026073008",
	swift: "CMFGUS33",
	bankName: "Community Federal Savings Bank",
} as const;

/**
 * SEPA membership and eurozone membership are living regulatory facts, not
 * static ones, countries join, leave, or switch currency over time. Source of
 * truth: the European Payments Council SEPA scheme country list (EPC409-09).
 * Snapshot verified current as of July 2026.
 *
 * The 41 participants of the SEPA schemes (EPC list).
 * 27 EU member states + 3 EEA (IS, LI, NO) + 11 non-EEA participants
 * (AD, AL, CH, GB, MC, MD, ME, MK, RS, SM, VA). Kept explicit rather than
 * derived, because "is this an EU country" and "is this a SEPA participant"
 * are different questions and the second one is the one that decides whether
 * a customer can send us a credit transfer at all.
 */
export const SEPA_COUNTRIES: ReadonlySet<string> = new Set([
	// EU-27
	"AT",
	"BE",
	"BG",
	"CY",
	"CZ",
	"DE",
	"DK",
	"EE",
	"ES",
	"FI",
	"FR",
	"GR",
	"HR",
	"HU",
	"IE",
	"IT",
	"LT",
	"LU",
	"LV",
	"MT",
	"NL",
	"PL",
	"PT",
	"RO",
	"SE",
	"SI",
	"SK",
	// EEA, non-EU
	"IS",
	"LI",
	"NO",
	// Non-EEA SEPA participants (microstates + Western Balkans + Moldova)
	"AD",
	"AL",
	"CH",
	"GB",
	"MC",
	"MD",
	"ME",
	"MK",
	"RS",
	"SM",
	"VA",
]);

/**
 * SEPA participants whose national currency is not the euro. Their banks
 * convert and may deduct fees, so these customers get the extra FX sentence on
 * the tile and are the expected source of small under-/overpayments.
 * (HR and BG are NOT here: Croatia adopted the euro in 2023, Bulgaria on
 * 1 January 2026. ME is NOT here either: Montenegro uses the euro
 * unilaterally without being a currency-union member.)
 */
export const SEPA_NON_EURO_COUNTRIES: ReadonlySet<string> = new Set([
	"AL", // ALL
	"CH", // CHF
	"CZ", // CZK
	"DK", // DKK
	"GB", // GBP
	"HU", // HUF
	"IS", // ISK
	"LI", // CHF
	"MD", // MDL
	"MK", // MKD
	"NO", // NOK
	"PL", // PLN
	"RO", // RON
	"RS", // RSD
	"SE", // SEK
]);

function normaliseCountryCode(countryCode: string | null | undefined): string | null {
	if (typeof countryCode !== "string") return null;
	const normalised = countryCode.trim().toUpperCase();
	return /^[A-Z]{2}$/.test(normalised) ? normalised : null;
}

/**
 * Fails closed: unknown, empty or malformed input is NOT eligible. Channel and
 * feature-flag gating live at the call site; this answers only "can this
 * country reach us with a SEPA credit transfer".
 */
export function isVorkasseEligibleCountry(countryCode: string | null | undefined): boolean {
	const normalised = normaliseCountryCode(countryCode);
	return normalised !== null && SEPA_COUNTRIES.has(normalised);
}

/** True for SEPA participants that settle in a currency other than EUR. */
export function isSepaNonEuroCountry(countryCode: string | null | undefined): boolean {
	const normalised = normaliseCountryCode(countryCode);
	return normalised !== null && SEPA_NON_EURO_COUNTRIES.has(normalised);
}

/**
 * Channel-aware eligibility, fails closed like isVorkasseEligibleCountry.
 * eu: any SEPA participant can send us a credit transfer. us: domestic ACH
 * only, so exactly the US — no territory list, a PR/GU/VI buyer on the us
 * channel still banks through US routing numbers via the US mainland system,
 * but their ISO codes are not "US" and stay excluded until the owner decides
 * otherwise.
 */
export function isVorkasseEligible(channel: string, countryCode: string | null | undefined): boolean {
	if (!isVorkasseChannel(channel)) return false;
	if (channel === "eu") return isVorkasseEligibleCountry(countryCode);
	return normaliseCountryCode(countryCode) === "US";
}
